import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  confirmEvidenceDownloadCompletionInAPI,
  convertToXml,
  EvidenceManifestInput,
  exportEvidenceManifestFromAPI,
  generateEvidenceManifestXml,
  getFinalXmlArchiveFromAPI,
  isContractParticipantIdentityExportError,
  isContractParticipantIdentityFinalXmlError,
  isEntitledPartyAccessExportError,
  isEntitledPartyAccessFinalXmlError,
  isFinalXmlRetrievalStateChangedError,
  isSupplementaryDocumentIntegrityLinkExportError,
  isSupplementaryDocumentIntegrityLinkFinalXmlError,
  isCmrArticle5CopyCoverageExportError,
  isEcmrArticle6ReceiptAccessHandoverExportError,
  isEcmrArticle6SupplementaryDocumentTransferExportError,
  isEvidenceManifestExportStateChangedError,
  loadDocumentFromSource,
  saveEvidenceManifestXmlContent,
  saveXmlContent,
} from '../services/eCMRService';
import { ECMR } from '../types/eCMR';
import { safeConsoleWarn } from '../utils/safeConsole';

type ECMRWithEvidence = ECMR & Partial<Omit<EvidenceManifestInput, 'documentId' | 'title'>>;

const buildEvidenceManifest = (ecmr: ECMR, documentId: string): EvidenceManifestInput => {
  const evidence = ecmr as ECMRWithEvidence;

  return {
    documentId,
    title: ecmr.ExchangedDocument?.ID || 'Sin título',
    evidenceSections: evidence.evidenceSections,
    currentVersion: evidence.currentVersion,
    ecmrArticle4Integrity: evidence.ecmrArticle4Integrity,
    procedureAcceptances: evidence.procedureAcceptances,
    signatureEvidence: evidence.signatureEvidence,
    supplementaryDocuments: evidence.supplementaryDocuments,
    carrierTakeoverCheckRecords: evidence.carrierTakeoverCheckRecords,
    carrierReservations: evidence.carrierReservations,
    disposalInstructionRecords: evidence.disposalInstructionRecords,
    disposalExecutionNoticeRecords: evidence.disposalExecutionNoticeRecords,
    carriageImpedimentRecords: evidence.carriageImpedimentRecords,
    carriageAccountingRecords: evidence.carriageAccountingRecords,
    carrierLiabilityIncidentRecords: evidence.carrierLiabilityIncidentRecords,
    deliveryTimingAssessmentRecords: evidence.deliveryTimingAssessmentRecords,
    compensationPaymentRecords: evidence.compensationPaymentRecords,
    recoveryDeliverySettlementRecords: evidence.recoveryDeliverySettlementRecords,
    recoveredGoodsDispositionRecords: evidence.recoveredGoodsDispositionRecords,
    codDeliveryOmissionRecords: evidence.codDeliveryOmissionRecords,
    dangerousGoodsNoticeRecords: evidence.dangerousGoodsNoticeRecords,
    compensationAssessmentRecords: evidence.compensationAssessmentRecords,
    compensationInterestRecords: evidence.compensationInterestRecords,
    liabilityLimitationRecords: evidence.liabilityLimitationRecords,
    claimReservationRecords: evidence.claimReservationRecords,
    disputeProcedureRecords: evidence.disputeProcedureRecords,
    successiveCarrierRecords: evidence.successiveCarrierRecords,
    multimodalCarriageRecords: evidence.multimodalCarriageRecords,
    consignmentNoteCopyRecords: evidence.consignmentNoteCopyRecords,
    ecmrReceiptAccessRequestRecords: evidence.ecmrReceiptAccessRequestRecords,
    ecmrReceiptAccessRecords: evidence.ecmrReceiptAccessRecords,
    senderParticularsResponsibilityRecords: evidence.senderParticularsResponsibilityRecords,
    prohibitedClauseRecords: evidence.prohibitedClauseRecords,
    consigneeDeliveryRequestRecords: evidence.consigneeDeliveryRequestRecords,
    xmlValidationRecords: evidence.xmlValidationRecords,
    cmrBusinessValidationRecords: evidence.cmrBusinessValidationRecords,
    ecmrProtocolValidationRecords: evidence.ecmrProtocolValidationRecords,
    deliveryConfirmation: evidence.deliveryConfirmation,
    amendmentRequests: evidence.amendmentRequests,
    retentionPolicy: evidence.retentionPolicy,
    legalHold: evidence.legalHold,
    auditEvents: evidence.auditEvents,
    documentTasks: evidence.documentTasks,
    immutableStorageReferences: evidence.immutableStorageReferences,
    sourceRecordOperationalEvidence: evidence.sourceRecordOperationalEvidence,
    evidenceDownloadCompletions: evidence.evidenceDownloadCompletions,
  };
};

const exportBaseFilename = (ecmr: ECMR): string =>
  ecmr.ExchangedDocument?.ID ? `ecmr_${ecmr.ExchangedDocument.ID}` : 'ecmr';

const GENERIC_LOAD_ERROR = 'No se pudo cargar el e-CMR para exportación.';

const ExportECMR: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ecmr, setEcmr] = useState<ECMR | null>(null);
  const [xml, setXml] = useState<string>('');
  const [evidenceManifest, setEvidenceManifest] = useState<EvidenceManifestInput | null>(null);
  const [evidenceManifestXml, setEvidenceManifestXml] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [finalXmlDownloadCompletionStatus, setFinalXmlDownloadCompletionStatus] = useState<'confirmed' | 'failed' | ''>('');
  const [evidenceManifestDownloadCompletionStatus, setEvidenceManifestDownloadCompletionStatus] = useState<'confirmed' | 'failed' | ''>('');
  const [finalXmlExportStatus, setFinalXmlExportStatus] = useState<
    | 'unavailable'
    | 'supplementary_integrity_required'
    | 'contract_participant_identity_required'
    | 'entitled_party_access_required'
    | 'state_changed'
    | ''
  >('');
  const [evidenceManifestExportStatus, setEvidenceManifestExportStatus] = useState<
    | 'unavailable'
    | 'supplementary_integrity_required'
    | 'cmr_article5_copy_coverage_required'
    | 'ecmr_article6_receipt_access_handover_required'
    | 'ecmr_article6_supplementary_document_transfer_required'
    | 'contract_participant_identity_required'
    | 'entitled_party_access_required'
    | 'state_changed'
    | ''
  >('');
  
  useEffect(() => {
    if (!id) {
      setError('ID de e-CMR no proporcionado');
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      try {
        const loadedEcmr = await loadDocumentFromSource(id, 'api');
        if (!loadedEcmr) {
          setError('e-CMR no encontrado');
          setLoading(false);
          return;
        }
        
        setEcmr(loadedEcmr);
        
        // Convertir a XML
        const generatedXml = await convertToXml(loadedEcmr);
        setXml(generatedXml);

        const generatedEvidenceManifest = buildEvidenceManifest(loadedEcmr, id);
        setEvidenceManifest(generatedEvidenceManifest);
        setEvidenceManifestXml(generateEvidenceManifestXml(generatedEvidenceManifest));
        
        setLoading(false);
      } catch (err) {
        setError(GENERIC_LOAD_ERROR);
        setLoading(false);
      }
    };
    
    loadData();
  }, [id]);
  
  const handleExport = async () => {
    if (!ecmr || !id) return;
    
    try {
      setFinalXmlExportStatus('');
      setFinalXmlDownloadCompletionStatus('');
      const archive = await getFinalXmlArchiveFromAPI(id);
      saveXmlContent(archive.xmlContent, `${exportBaseFilename(ecmr)}_final_archived.xml`);

      const evidenceType = archive.archiveReference?.evidenceType;
      if (evidenceType === 'final_xml') {
        try {
          await confirmEvidenceDownloadCompletionInAPI(id, { evidenceType });
          setFinalXmlDownloadCompletionStatus('confirmed');
        } catch (_completionErr) {
          setFinalXmlDownloadCompletionStatus('failed');
        }
      }
    } catch (exportError) {
      setFinalXmlExportStatus(
        isContractParticipantIdentityFinalXmlError(exportError)
          ? 'contract_participant_identity_required'
          : isEntitledPartyAccessFinalXmlError(exportError)
          ? 'entitled_party_access_required'
          : isSupplementaryDocumentIntegrityLinkFinalXmlError(exportError)
          ? 'supplementary_integrity_required'
          : isFinalXmlRetrievalStateChangedError(exportError)
          ? 'state_changed'
          : 'unavailable'
      );
    }
  };

  const handleExportEvidenceManifest = async () => {
    if (!ecmr || !evidenceManifest || !id) return;

    const filename = `${exportBaseFilename(ecmr)}_evidence_manifest.xml`;

    try {
      setEvidenceManifestDownloadCompletionStatus('');
      setEvidenceManifestExportStatus('');
      const authoritativeManifest = await exportEvidenceManifestFromAPI(id);
      saveEvidenceManifestXmlContent(authoritativeManifest.evidenceManifestXml, filename);
      const evidenceType = authoritativeManifest.archiveReference?.evidenceType;
      if (evidenceType === 'evidence_manifest_xml') {
        try {
          await confirmEvidenceDownloadCompletionInAPI(id, { evidenceType });
          setEvidenceManifestDownloadCompletionStatus('confirmed');
        } catch (completionErr) {
          safeConsoleWarn('No se pudo confirmar la descarga completa del manifiesto de evidencias', 'evidence_manifest.download_completion.confirm');
          setEvidenceManifestDownloadCompletionStatus('failed');
        }
      }
    } catch (exportError) {
      setEvidenceManifestExportStatus(
        isContractParticipantIdentityExportError(exportError)
          ? 'contract_participant_identity_required'
          : isEntitledPartyAccessExportError(exportError)
          ? 'entitled_party_access_required'
          : isSupplementaryDocumentIntegrityLinkExportError(exportError)
          ? 'supplementary_integrity_required'
          : isCmrArticle5CopyCoverageExportError(exportError)
          ? 'cmr_article5_copy_coverage_required'
          : isEcmrArticle6ReceiptAccessHandoverExportError(exportError)
          ? 'ecmr_article6_receipt_access_handover_required'
          : isEcmrArticle6SupplementaryDocumentTransferExportError(exportError)
          ? 'ecmr_article6_supplementary_document_transfer_required'
          : isEvidenceManifestExportStateChangedError(exportError)
          ? 'state_changed'
          : 'unavailable'
      );
    }
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <div className="text-center">
          <p className="text-lg">Cargando...</p>
        </div>
      </div>
    );
  }
  
  if (error || !ecmr) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error || 'Error al cargar el e-CMR'}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Volver a la lista
        </button>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">
          Exportar e-CMR: {ecmr.ExchangedDocument?.ID || 'Sin título'}
        </h1>
        
        <div className="flex space-x-2">
          <button
            onClick={handleExport}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
          >
            Descargar XML final
          </button>

          <button
            onClick={handleExportEvidenceManifest}
            className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
          >
            Descargar manifiesto de evidencias
          </button>
          
          <button
            onClick={() => navigate(`/view/${id}`)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Volver
          </button>
        </div>
      </div>
      
      <div className="bg-white shadow-md rounded p-4 overflow-auto text-sm mb-4">
        <h2 className="text-xl font-semibold mb-4">Vista previa XML local/no autoritativa</h2>
        <p className="text-sm text-gray-700 mb-4">
          La descarga usa el XML final archivado del backend. Esta vista previa no sustituye las compuertas autoritativas de validación, firma, aceptación de procedimientos y archivo.
        </p>
        {finalXmlExportStatus === 'unavailable' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            XML final archivado no disponible. No se generó una descarga local no autoritativa; revise las tareas de archivo, firma y aceptación de procedimientos.
          </p>
        )}
        {finalXmlExportStatus === 'state_changed' && (
          <p className="mb-4 rounded border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
            El documento cambio mientras se recuperaba el XML final. Recargue la version vigente antes de volver a descargar; no se guardo XML ni se confirmo la descarga.
          </p>
        )}
        {finalXmlExportStatus === 'supplementary_integrity_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            XML final archivado bloqueado por documentos suplementarios sin enlace de integridad vigente. Revise los adjuntos para confirmar SHA-256, almacenamiento backend y version actual antes de exportar.{' '}
            <Link to={`/attachments/${id}`} className="font-medium underline">Gestionar adjuntos</Link>
          </p>
        )}
        {finalXmlExportStatus === 'contract_participant_identity_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            XML final archivado bloqueado hasta identificar al remitente y al transportista.{' '}
            <Link to={`/workflow/${id}`} className="font-medium underline">Vincular identidades</Link>
          </p>
        )}
        {finalXmlExportStatus === 'entitled_party_access_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            XML final archivado bloqueado hasta restablecer el acceso backend de las partes eCMR identificadas.{' '}
            <Link to={`/workflow/${id}`} className="font-medium underline">Restablecer acceso</Link>
          </p>
        )}
        {finalXmlDownloadCompletionStatus === 'confirmed' && (
          <p className="mb-4 rounded border-l-4 border-green-500 bg-green-50 p-3 text-sm text-green-800">
            Descarga de XML final confirmada como evidencia de baseline técnico parcial.
          </p>
        )}
        {finalXmlDownloadCompletionStatus === 'failed' && (
          <p className="mb-4 rounded border-l-4 border-yellow-500 bg-yellow-50 p-3 text-sm text-yellow-800">
            Descarga realizada, pero no se pudo confirmar la evidencia de finalización; el XML final descargado no se bloqueó.
          </p>
        )}
        <pre className="whitespace-pre-wrap">{xml}</pre>
      </div>

      <div className="bg-white shadow-md rounded p-4 overflow-auto text-sm">
        <h2 className="text-xl font-semibold mb-4">Vista previa manifiesto de evidencias</h2>
        <p className="text-sm text-gray-700 mb-4">
          Vista previa local/no autoritativa del baseline técnico parcial: referencia evidencias operativas sin incrustar payloads, adjuntos ni metadatos sensibles. La descarga usa el manifiesto autoritativo del backend.
        </p>
        {evidenceManifestExportStatus === 'unavailable' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            Manifiesto autoritativo de evidencias no disponible. No se generó una descarga local no autoritativa; inténtelo de nuevo más tarde o contacte soporte.
          </p>
        )}
        {evidenceManifestExportStatus === 'supplementary_integrity_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            Manifiesto autoritativo bloqueado por documentos suplementarios sin enlace de integridad vigente. Revise los adjuntos para confirmar SHA-256, almacenamiento backend y version actual antes de exportar.{' '}
            <Link to={`/attachments/${id}`} className="font-medium underline">Gestionar adjuntos</Link>
          </p>
        )}
        {evidenceManifestExportStatus === 'state_changed' && (
          <p className="mb-4 rounded border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-900">
            El documento cambio mientras se archivaba el manifiesto. Recargue la version vigente antes de volver a exportar; no se confirmo ninguna descarga ni referencia autoritativa.
          </p>
        )}
        {evidenceManifestExportStatus === 'cmr_article5_copy_coverage_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            Manifiesto autoritativo bloqueado hasta registrar las tres copias CMR de la version actual con acceso equivalente atribuible al remitente y al transportista.{' '}
            <Link to={`/evidence/${id}`} className="font-medium underline">Registrar copias y acceso</Link>
          </p>
        )}
        {evidenceManifestExportStatus === 'ecmr_article6_receipt_access_handover_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            Manifiesto autoritativo bloqueado porque una solicitud vigente del remitente sigue pendiente de entrega por el transportista. El XML final permanece disponible para completar el acceso solicitado.{' '}
            <Link to={`/evidence/${id}`} className="font-medium underline">Registrar entrega de recibo y acceso</Link>
          </p>
        )}
        {evidenceManifestExportStatus === 'ecmr_article6_supplementary_document_transfer_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            Manifiesto autoritativo bloqueado hasta que el remitente aporte cada documento suplementario electronico y el transportista confirme su descarga. El XML final permanece disponible.{' '}
            <Link to={`/attachments/${id}`} className="font-medium underline">Gestionar entrega de adjuntos</Link>
          </p>
        )}
        {evidenceManifestExportStatus === 'contract_participant_identity_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            Manifiesto autoritativo bloqueado hasta identificar al remitente y al transportista.{' '}
            <Link to={`/workflow/${id}`} className="font-medium underline">Vincular identidades</Link>
          </p>
        )}
        {evidenceManifestExportStatus === 'entitled_party_access_required' && (
          <p className="mb-4 rounded border-l-4 border-red-500 bg-red-50 p-3 text-sm text-red-800">
            Manifiesto autoritativo bloqueado hasta restablecer el acceso backend de las partes eCMR identificadas.{' '}
            <Link to={`/workflow/${id}`} className="font-medium underline">Restablecer acceso</Link>
          </p>
        )}
        {evidenceManifestDownloadCompletionStatus === 'confirmed' && (
          <p className="mb-4 rounded border-l-4 border-green-500 bg-green-50 p-3 text-sm text-green-800">
            Descarga confirmada como evidencia de baseline técnico parcial.
          </p>
        )}
        {evidenceManifestDownloadCompletionStatus === 'failed' && (
          <p className="mb-4 rounded border-l-4 border-yellow-500 bg-yellow-50 p-3 text-sm text-yellow-800">
            Descarga realizada, pero no se pudo confirmar la evidencia de finalización; el manifiesto descargado no se bloqueó.
          </p>
        )}
        <pre className="whitespace-pre-wrap">{evidenceManifestXml}</pre>
      </div>
    </div>
  );
};

export default ExportECMR;
